import api from './api';
import type {
  User,
  CreateUserRequest,
  UpdateUserRequest,
  PaginatedResponse,
  PaginationParams,
  AuditLogEntry,
  AuditLogFilters,
  SystemSetting,
} from '../types';

export const settingsService = {
  // System settings
  async getSettings(): Promise<SystemSetting[]> {
    const response = await api.get('/settings');
    const raw = response.data;
    // API returns array directly for settings
    if (Array.isArray(raw)) return raw;
    if (raw && Array.isArray(raw.data)) return raw.data;
    return [];
  },

  async updateSetting(key: string, value: string): Promise<SystemSetting> {
    const response = await api.put<SystemSetting>(`/settings/${key}`, { value });
    return response.data;
  },

  // User management
  async getUsers(params?: PaginationParams): Promise<PaginatedResponse<User>> {
    const response = await api.get('/users', { params });
    const raw = response.data;
    // API returns { data: [...], pagination: { page, limit, total, totalPages } }
    if (raw && raw.pagination) {
      return {
        data: raw.data ?? [],
        page: raw.pagination.page ?? 1,
        pageSize: raw.pagination.limit ?? 100,
        total: raw.pagination.total ?? 0,
        totalPages: raw.pagination.totalPages ?? 1,
      };
    }
    if (Array.isArray(raw)) {
      return {
        data: raw,
        page: 1,
        pageSize: raw.length,
        total: raw.length,
        totalPages: 1,
      };
    }
    return {
      data: raw?.data ?? [],
      page: raw?.page ?? 1,
      pageSize: raw?.pageSize ?? raw?.limit ?? 100,
      total: raw?.total ?? 0,
      totalPages: raw?.totalPages ?? 1,
    };
  },

  async createUser(data: CreateUserRequest): Promise<User> {
    const response = await api.post<User>('/users', data);
    return response.data;
  },

  async updateUser(id: string, data: UpdateUserRequest): Promise<User> {
    const response = await api.put<User>(`/users/${id}`, data);
    return response.data;
  },

  async deleteUser(id: string): Promise<void> {
    await api.delete(`/users/${id}`);
  },

  // Audit log
  async getAuditLog(params?: AuditLogFilters): Promise<PaginatedResponse<AuditLogEntry>> {
    const response = await api.get('/audit-log', { params });
    const raw = response.data;
    // API returns { data: [...], pagination: { page, limit, total, totalPages } }
    if (raw && raw.pagination) {
      return {
        data: raw.data ?? [],
        page: raw.pagination.page ?? 1,
        pageSize: raw.pagination.limit ?? 100,
        total: raw.pagination.total ?? 0,
        totalPages: raw.pagination.totalPages ?? 1,
      };
    }
    if (Array.isArray(raw)) {
      return {
        data: raw,
        page: 1,
        pageSize: raw.length,
        total: raw.length,
        totalPages: 1,
      };
    }
    return {
      data: raw?.data ?? [],
      page: raw?.page ?? 1,
      pageSize: raw?.pageSize ?? raw?.limit ?? 100,
      total: raw?.total ?? 0,
      totalPages: raw?.totalPages ?? 1,
    };
  },
};
